import React, { useState, useEffect } from "react";

/**
 * CookieBanner
 * -------------
 * A GDPR/PECR-style cookie consent banner with Accept all / Reject
 * non-essential / Manage preferences options.
 *
 * How it works:
 * - On mount, checks localStorage for an existing consent record.
 * - If none exists, shows the banner.
 * - Consent choice is saved to localStorage AND broadcast as a
 *   "cookieConsentChange" window event, so any other part of the app
 *   (e.g. your Google Analytics loader) can react to it without being
 *   tightly coupled to this component.
 *
 * Wiring up Google Analytics:
 * 1. Do NOT hardcode the GA script tag in index.html or _document — that
 *    loads GA unconditionally, before the user has made a choice.
 * 2. Instead, call `loadGoogleAnalytics()` (exported below) only when
 *    consent.analytics is true. See the bottom of this file for a
 *    ready-to-use loader function and a `useCookieConsent()` hook you
 *    can drop into App.jsx to trigger it.
 */

const STORAGE_KEY = "solksites_cookie_consent";
const CONSENT_EVENT = "cookieConsentChange";

function getStoredConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storeConsent(consent) {
  const record = { ...consent, essential: true, timestamp: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: record }));
  return record;
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  useEffect(() => {
    const existing = getStoredConsent();
    if (!existing) setVisible(true);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    storeConsent({ analytics: true });
    setVisible(false);
  };

  const rejectNonEssential = () => {
    storeConsent({ analytics: false });
    setVisible(false);
  };

  const savePreferences = () => {
    storeConsent({ analytics: analyticsEnabled });
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      className="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6"
    >
      <div className="container-page">
        <div className="max-w-3xl mx-auto rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl p-5 sm:p-6">
          <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            We use essential cookies to make this site work, and (with your permission) Google
            Analytics cookies to understand how visitors use it.{" "}
            <a href="/cookies" className="underline font-medium text-slate-900 dark:text-white">
              Read our cookie policy
            </a>{" "}
            for more information.
          </p>

          {expanded && (
            <div className="mt-4 space-y-3 border-t border-slate-100 dark:border-slate-800 pt-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">Essential cookies</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Always on — required for the site to function.</p>
                </div>
                <span className="text-xs font-medium text-slate-400 dark:text-slate-500">Always active</span>
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">Analytics cookies</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Google Analytics — helps us understand site usage.</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={analyticsEnabled}
                  onClick={() => setAnalyticsEnabled((v) => !v)}
                  className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                    analyticsEnabled ? "bg-slate-900 dark:bg-white" : "bg-slate-200 dark:bg-slate-700"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white dark:bg-slate-900 transition-transform ${
                      analyticsEnabled ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={acceptAll}
              className="px-4 py-2 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-semibold hover:opacity-90 transition"
            >
              Accept all
            </button>
            <button
              type="button"
              onClick={rejectNonEssential}
              className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition"
            >
              Reject non-essential
            </button>
            {!expanded ? (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 text-sm font-medium hover:text-slate-900 dark:hover:text-white transition"
              >
                Manage preferences
              </button>
            ) : (
              <button
                type="button"
                onClick={savePreferences}
                className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 text-sm font-medium hover:text-slate-900 dark:hover:text-white transition"
              >
                Save preferences
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * loadGoogleAnalytics
 * --------------------
 * Injects the GA4 script tag and initialises gtag. Call this ONLY after
 * consent.analytics === true. Replace GA_MEASUREMENT_ID with your real ID
 * (format: G-XXXXXXXXXX) from Google Analytics > Admin > Data Streams.
 */
export function loadGoogleAnalytics(measurementId = "G-XXXXXXXXXX") {
  if (window.__gaLoaded) return; // avoid loading twice
  window.__gaLoaded = true;

  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.async = true;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", measurementId);
}

/**
 * useCookieConsent
 * ------------------
 * Drop this into App.jsx (top level, once) to automatically load GA
 * whenever the user has consented — whether that happens on first visit
 * or later via "Manage preferences".
 *
 * Example usage in App.jsx:
 *
 *   import { useCookieConsent, loadGoogleAnalytics } from "./components/CookieBanner";
 *
 *   function App() {
 *     useCookieConsent((consent) => {
 *       if (consent.analytics) loadGoogleAnalytics("G-XXXXXXXXXX");
 *     });
 *     ...
 *   }
 */
export function useCookieConsent(onChange) {
  useEffect(() => {
    const existing = getStoredConsent();
    if (existing) onChange(existing);

    const handler = (e) => onChange(e.detail);
    window.addEventListener(CONSENT_EVENT, handler);
    return () => window.removeEventListener(CONSENT_EVENT, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
